import { isValidElement, useId, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronLeft, faChevronRight, faGear, faPlay, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useHiddenColumns } from '../../hooks/useHiddenColumns';
import { useMaxWidth } from '../../hooks/usePhone';
import { TABLET, pillSt } from '../../theme/tokens';
import * as S from './styled';

const PAGE_SIZES = [10, 20, 50, 100];

function pageNumbers(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (cur <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (cur >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', cur - 1, cur, cur + 1, '…', total];
}

const isCell = (v) => v != null && typeof v === 'object' && !isValidElement(v) && 'kind' in v;

function Cell({ value }) {
  if (!isCell(value)) return value ?? null;
  const { kind, text, sub, strong, nowrap, tabular, ellipsis, color, pills = [], href = '#', onClick } = value;
  const textProps = { $strong: strong, $nowrap: nowrap, $tabular: tabular };
  switch (kind) {
    case 'stacked':
      return (
        <div>
          <S.Text as="div" {...textProps}>
            {text}
          </S.Text>
          <S.Sub>{sub}</S.Sub>
        </div>
      );
    case 'pill':
      return <span style={pillSt(color)}>{text}</span>;
    case 'pills':
      return (
        <S.Pills>
          {pills.map((p) => (
            <span key={p.label} style={pillSt(p.color)}>
              {p.label}
            </span>
          ))}
        </S.Pills>
      );
    case 'link':
      return (
        <div>
          <S.Link href={href} onClick={onClick}>
            {text}
          </S.Link>
          <S.Sub>{sub}</S.Sub>
        </div>
      );
    case 'play':
      return (
        <S.Play>
          <a href="#recording" onClick={onClick}>
            <FontAwesomeIcon icon={faPlay} />
            Play
          </a>
          <span>{sub}</span>
        </S.Play>
      );
    case 'node':
      return <div>{value.node}</div>;
    default:
      return ellipsis ? (
        <S.Text {...textProps} $ellipsis title={text}>
          {text}
        </S.Text>
      ) : (
        <S.Text {...textProps}>{text}</S.Text>
      );
  }
}

function ColumnsGear({ columns, hidden, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <S.Gear type="button" $open={open} aria-expanded={open} aria-label="Edit columns" title="Edit columns" onClick={() => setOpen((o) => !o)}>
        <FontAwesomeIcon icon={faGear} />
      </S.Gear>
      {open && (
        <S.ColMenu role="dialog" aria-label="Columns">
          <S.ColMenuHead>
            <span>Columns</span>
            <button type="button" aria-label="Close columns" onClick={() => setOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </S.ColMenuHead>
          <S.ColList>
            {columns.map((c) => {
              const checked = !hidden.includes(c.key);
              return (
                <S.ColOption key={c.key} $locked={c.locked}>
                  <input type="checkbox" checked={checked} disabled={c.locked} onChange={() => onToggle(c.key)} />
                  <S.Box $checked={checked} aria-hidden="true">
                    <FontAwesomeIcon icon={faCheck} />
                  </S.Box>
                  {c.header}
                </S.ColOption>
              );
            })}
          </S.ColList>
          <S.Reset type="button" onClick={onReset}>
            Reset to default
          </S.Reset>
        </S.ColMenu>
      )}
    </>
  );
}

function Pager({ page, size, total, onChange }) {
  const pages = Math.max(1, Math.ceil(total / size));
  const start = (page - 1) * size;
  return (
    <S.Pager>
      <S.PagerNote>{`${start + 1}–${Math.min(start + size, total)} of ${total}`}</S.PagerNote>
      <S.Pages aria-label="Pagination">
        <S.NavBtn type="button" aria-label="Previous page" disabled={page === 1} onClick={() => onChange(page - 1, size)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </S.NavBtn>
        {pageNumbers(page, pages).map((n, i) =>
          n === '…' ? (
            <S.PageBtn as="span" key={`gap-${i}`} $gap>
              …
            </S.PageBtn>
          ) : (
            <S.PageBtn
              type="button"
              key={n}
              $active={n === page}
              aria-current={n === page ? 'page' : undefined}
              onClick={() => onChange(n, size)}
            >
              {n}
            </S.PageBtn>
          )
        )}
        <S.NavBtn type="button" aria-label="Next page" disabled={page === pages} onClick={() => onChange(page + 1, size)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </S.NavBtn>
      </S.Pages>
      <S.SizeSelect>
        <select aria-label="Rows per page" value={size} onChange={(e) => onChange(1, Number(e.target.value))}>
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
      </S.SizeSelect>
    </S.Pager>
  );
}

const activate = (handler, row) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler(row);
  }
};

const valueOf = (c, row) => (c.render ? c.render(row) : row[c.key]);

const blank = (v) => v == null || v === '' || v === '—' || v.text === '—' || v.text === '' || (v.kind === 'pills' && !v.pills?.length);
const isPill = (v) => isCell(v) && (v.kind === 'pill' || v.kind === 'pills');
const metaText = (v) => (typeof v === 'string' || typeof v === 'number' ? v : isCell(v) && ['text', 'stacked', 'link'].includes(v.kind) ? v.text : null);

function DefaultCard({ cols, row }) {
  const [lead, ...vals] = cols.map((c) => valueOf(c, row)).filter((v, i) => i === 0 || !blank(v));
  const [top, ...pills] = vals.filter(isPill);
  const rest = vals.filter((v) => !isPill(v));
  const meta = rest.map(metaText).filter((t) => t != null);
  const extra = rest.filter((v) => metaText(v) == null);
  return (
    <S.CardMain>
      <S.CardTop>
        <S.CardLead>
          <Cell value={lead} />
        </S.CardLead>
        {top && <Cell value={top} />}
      </S.CardTop>
      {meta.length > 0 && <S.CardMeta>{meta.join(' · ')}</S.CardMeta>}
      {pills.length + extra.length > 0 && (
        <S.Pills>
          {[...pills, ...extra].map((v, i) => (
            <Cell key={i} value={v} />
          ))}
        </S.Pills>
      )}
    </S.CardMain>
  );
}

function CardList({ columns, rows, loading, skeletonRows, rowKey, rowLabel, selectedKey, onRowClick, phoneCard }) {
  const cols = columns.filter((c) => !c.hideOnPhone);
  const clickable = Boolean(onRowClick);
  if (loading) {
    return (
      <S.Cards aria-busy="true">
        {Array.from({ length: skeletonRows }, (_, r) => (
          <S.CardRow key={`sk-${r}`}>
            <S.CardMain>
              <S.SkeletonBar />
              <S.SkeletonBar />
            </S.CardMain>
          </S.CardRow>
        ))}
      </S.Cards>
    );
  }
  return (
    <S.Cards>
      {rows.map((row) => (
        <S.CardRow
          key={rowKey(row)}
          $clickable={clickable}
          $selected={selectedKey != null && rowKey(row) === selectedKey}
          tabIndex={clickable ? 0 : undefined}
          role={clickable ? 'button' : undefined}
          aria-label={clickable && rowLabel ? rowLabel(row) : undefined}
          onClick={clickable ? () => onRowClick(row) : undefined}
          onKeyDown={clickable ? activate(onRowClick, row) : undefined}
        >
          {phoneCard ? phoneCard(row) : <DefaultCard cols={cols} row={row} />}
          {clickable && <S.CardChevron icon={faChevronRight} />}
        </S.CardRow>
      ))}
    </S.Cards>
  );
}

export default function DataTable({
  id,
  title,
  note,
  actions,
  columns,
  data = [],
  loading = false,
  onRowClick,
  rowKey = (r) => r.id,
  rowLabel,
  selectedKey,
  minWidth = 720,
  skeletonRows = 5,
  emptyText = 'Nothing to show.',
  pagination,
  paginate = true,
  phoneCard
}) {
  const titleId = useId();
  const cards = useMaxWidth(TABLET);
  const { hidden, toggle, reset } = useHiddenColumns(`${id}HiddenCols`);
  const visible = useMemo(() => (id ? columns.filter((c) => !hidden.includes(c.key)) : columns), [id, columns, hidden]);
  const [local, setLocal] = useState({ page: 1, size: PAGE_SIZES[0] });

  const size = pagination ? pagination.pageSize : local.size;
  const total = pagination ? pagination.total : data.length;
  const page = Math.min(pagination ? pagination.page : local.page, Math.max(1, Math.ceil(total / size)));
  const rows = pagination || !paginate ? data : data.slice((page - 1) * size, page * size);
  const onPage = pagination ? pagination.onChange : (p, s) => setLocal({ page: p, size: s });

  const last = visible.length - 1;
  const pad = (i) => (i === 0 || i === last ? 20 : 12);
  const clickable = Boolean(onRowClick);

  return (
    <S.Card aria-labelledby={title ? titleId : undefined}>
      {(title || actions || id) && (
        <S.Head>
          <S.HeadText>
            <h2 id={titleId}>{title}</h2>
            <span aria-live="polite">{note}</span>
          </S.HeadText>
          <S.HeadTools>
            {actions}
            {id && <ColumnsGear columns={columns} hidden={hidden} onToggle={toggle} onReset={reset} />}
          </S.HeadTools>
        </S.Head>
      )}
      {cards ? (
        <CardList
          columns={visible}
          rows={rows}
          loading={loading}
          skeletonRows={skeletonRows}
          rowKey={rowKey}
          rowLabel={rowLabel}
          selectedKey={selectedKey}
          onRowClick={onRowClick}
          phoneCard={phoneCard}
        />
      ) : (
        <S.Scroll>
          <S.Table $minWidth={minWidth} aria-busy={loading}>
            <thead>
              <S.HeadRow>
                {visible.map((c, i) => (
                  <S.Th key={c.key} scope="col" $pad={pad(i)} $align={c.align} style={c.width ? { width: c.width } : undefined}>
                    {c.header}
                  </S.Th>
                ))}
              </S.HeadRow>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: skeletonRows }, (_, r) => (
                    <S.Row key={`sk-${r}`}>
                      {visible.map((c, i) => (
                        <S.Td key={c.key} $pad={pad(i)}>
                          <S.SkeletonBar />
                        </S.Td>
                      ))}
                    </S.Row>
                  ))
                : rows.map((row) => (
                    <S.Row
                      key={rowKey(row)}
                      $clickable={clickable}
                      $selected={selectedKey != null && rowKey(row) === selectedKey}
                      tabIndex={clickable ? 0 : undefined}
                      aria-label={clickable && rowLabel ? rowLabel(row) : undefined}
                      onClick={clickable ? () => onRowClick(row) : undefined}
                      onKeyDown={clickable ? activate(onRowClick, row) : undefined}
                    >
                      {visible.map((c, i) => (
                        <S.Td key={c.key} $pad={pad(i)} $align={c.align}>
                          <Cell value={valueOf(c, row)} />
                        </S.Td>
                      ))}
                    </S.Row>
                  ))}
            </tbody>
          </S.Table>
        </S.Scroll>
      )}
      {!loading && !total && <S.Empty>{emptyText}</S.Empty>}
      {paginate && !loading && total > 0 && <Pager page={page} size={size} total={total} onChange={onPage} />}
    </S.Card>
  );
}
